import { useMarkAppointmentCompletedMutation } from '../../generated/graphql';

import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useAppDependenciesContext } from '../../state';
import { normalizeError } from '../../utils';
import type { RoomDelegator } from '../../video-sdk';
import {
  ProviderState,
  inSessionPatientInformationAtom,
  prevSessionPatientInfoAtom,
  providerStateAtom,
} from '../state';
import { useProviderDisconnectRoomCallback } from '../useProviderDisconnectRoomCallback';

export type CompleteSessionCallback = () => Promise<RoomDelegator | undefined>;

/**
 * Returns a callback that handles all fire-and-forget, session completion
 * side-effects e.g. marking the appointment as completed.
 */
function useHandleSessionCompletionSideEffectsCallback() {
  const [markAppointmentCompletedMutation] = useMarkAppointmentCompletedMutation();
  const { logger } = useAppDependenciesContext();

  return useCallback(
    async (appointmentShortId?: string) => {
      if (!appointmentShortId) {
        logger.error('Unable to mark appointment as completed: missing appointment short ID');
        return;
      }
      try {
        await markAppointmentCompletedMutation({
          variables: {
            appointmentShortId,
          },
        });
      } catch (error) {
        logger.error('Failed to mark appointment as completed', { appointmentShortId }, normalizeError(error));
      }
    },
    [logger, markAppointmentCompletedMutation],
  );
}

/**
 * Returns a callback function that disconnects the current session room and
 * marks the appointment completed.
 */
export function useCompleteSessionCallback(): CompleteSessionCallback {
  const disconnectRoom = useProviderDisconnectRoomCallback();
  const setProviderState = useSetAtom(providerStateAtom);
  const setPrevSessionPatientInfo = useSetAtom(prevSessionPatientInfoAtom);
  const getInSessionPatientInformation = useAtomCallbackMemoized((get) => get(inSessionPatientInformationAtom), []);
  const handleSessionCompletionSideEffects = useHandleSessionCompletionSideEffectsCallback();

  const callback = useCallback<CompleteSessionCallback>(
    async () => {
      // Important to read this before disconnecting from the room, which clears it
      const inSessionPatientInformation = getInSessionPatientInformation();
      const appointmentShortId = inSessionPatientInformation?.inSessionAppointmentShortId;
      const room = await disconnectRoom();
      if (!room) return;

      setProviderState(ProviderState.COMPLETED);
      setPrevSessionPatientInfo(inSessionPatientInformation);

      // Intentionally not awaited
      handleSessionCompletionSideEffects(appointmentShortId);

      return room;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return callback;
}
